import { computed, onUnmounted, ref, shallowRef } from 'vue'
import {
  fingerprintFile,
  initialIntegrityState,
  integrityStateForFileOffset,
  updateIntegrityChain,
} from '../lib/transferIntegrity.js'
import {
  deleteRoomSession,
  getRoomSession,
  getTransferRecord,
  openReceiverStorage,
  saveRoomSession,
  saveTransferRecord,
} from '../lib/transferStorage.js'
import { useDirectSecureTransferStore } from '../stores/directSecureTransferStore.js'

const SIGNAL_EVENT_TYPES = {
  offer: 'offer',
  answer: 'answer',
  iceCandidate: 'ice-candidate',
  peerReady: 'peer-ready',
  resumeRequest: 'resume-request',
  transferCancelled: 'transfer-cancelled',
}

const connectionReadyStates = new Set(['connected', 'completed'])
const transferInProgressStates = new Set([
  'preparing',
  'waiting-for-receiver',
  'sending',
  'receiving',
  'resuming',
  'reconnecting',
  'verifying',
  'awaiting-verification',
])

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const sanitizedFileName = (fileName) => {
  const sanitized = String(fileName ?? '')
    .replace(/[\u0000-\u001f\u007f/\\]/g, '_')
    .replace(/^\.+/, '')
    .trim()
    .slice(0, 180)

  return sanitized || 'transferred-file'
}

const errorMessageFrom = (error, fallback) => {
  return error?.response?.data?.message
    ?? Object.values(error?.response?.data?.errors ?? {})?.[0]?.[0]
    ?? error?.message
    ?? fallback
}

const validOffset = (offset, size, chunkSize) => {
  return Number.isSafeInteger(offset)
    && offset >= 0
    && offset <= size
    && (offset === size || offset % chunkSize === 0)
}

const normalizedMetadata = (value, maxFileSize) => {
  const metadata = value && typeof value === 'object' ? value : null

  if (
    !metadata
    || !uuidPattern.test(metadata.transferId ?? '')
    || !Number.isSafeInteger(metadata.size)
    || metadata.size < 0
    || metadata.size > maxFileSize
    || !Number.isSafeInteger(metadata.chunkSize)
    || metadata.chunkSize < 16 * 1024
    || metadata.chunkSize > 256 * 1024
    || !Number.isSafeInteger(metadata.lastModified)
    || !/^[a-f0-9]{64}$/i.test(metadata.fingerprint ?? '')
  ) {
    return null
  }

  return {
    transferId: metadata.transferId,
    fileName: sanitizedFileName(metadata.fileName),
    mimeType: String(metadata.mimeType ?? '').slice(0, 255),
    size: metadata.size,
    chunkSize: metadata.chunkSize,
    lastModified: metadata.lastModified,
    fingerprint: metadata.fingerprint,
    integrityAlgorithm: 'sha256-chain-v1',
  }
}

const metadataMatchesRecord = (metadata, record) => {
  return record?.size === metadata.size
    && record?.chunkSize === metadata.chunkSize
    && record?.lastModified === metadata.lastModified
    && record?.fingerprint === metadata.fingerprint
}

export function useDirectSecureTransfer() {
  const transferStore = useDirectSecureTransferStore()
  const peerConnection = shallowRef(null)
  const dataChannel = shallowRef(null)
  const selectedFile = shallowRef(null)
  const outgoingMetadata = ref(null)
  const incomingMetadata = ref(null)
  const connectionState = ref('idle')
  const transferStatus = ref('idle')
  const routeType = ref('unknown')
  const bytesTransferred = ref(0)
  const totalBytes = ref(0)
  const speedBytesPerSecond = ref(0)
  const integrityStatus = ref('pending')
  const errorMessage = ref('')
  const downloadAvailable = ref(false)
  const storageMode = ref(null)
  const hasStartedTransfer = ref(false)
  const selectedFileCanResume = ref(false)

  const isCreator = computed(() => transferStore.room?.role === 'creator')
  const isRecipient = computed(() => transferStore.room?.role === 'recipient')
  const isParticipant = computed(() => isCreator.value || isRecipient.value)
  const isConnected = computed(() => connectionReadyStates.has(connectionState.value))
  const isTransferInProgress = computed(() => transferInProgressStates.has(transferStatus.value))
  const progress = computed(() => {
    if (totalBytes.value <= 0) {
      return 0
    }

    return Math.min(100, Math.round((bytesTransferred.value / totalBytes.value) * 1000) / 10)
  })

  let receiverStorage = null
  let receiverIntegrityState = initialIntegrityState()
  let senderIntegrityState = initialIntegrityState()
  let senderHashedOffset = 0
  let sendGeneration = 0
  let receiveQueue = Promise.resolve()
  let pendingIceCandidates = []
  let makingOffer = false
  let reconnectTimer = null
  let reconnectFallbackTimer = null
  let statsTimer = null
  let speedTimer = null
  let previousSpeedBytes = 0
  let previousSpeedTime = Date.now()
  let isReplacingConnection = false
  let isCleaningUp = false

  const transferSettings = () => {
    const configuredChunkSize = Number(transferStore.room?.transfer?.chunk_size ?? 64 * 1024)
    const maxBufferedAmount = Math.max(256 * 1024, Number(
      transferStore.room?.transfer?.max_buffered_amount ?? 4 * 1024 * 1024
    ))
    const bufferedAmountLowThreshold = Math.min(maxBufferedAmount / 2, Math.max(64 * 1024, Number(
      transferStore.room?.transfer?.buffered_amount_low_threshold ?? 1024 * 1024
    )))

    return {
      maxFileSize: Number(transferStore.room?.transfer?.max_file_size ?? 0),
      chunkSize: Math.min(256 * 1024, Math.max(16 * 1024, configuredChunkSize)),
      bufferedAmountLowThreshold,
      maxBufferedAmount,
    }
  }

  const setError = (error, fallback = 'The direct transfer could not continue.') => {
    errorMessage.value = errorMessageFrom(error, fallback)

    if (isTransferInProgress.value) {
      transferStatus.value = 'failed'
    }
  }

  const clearConnectionTimers = () => {
    clearTimeout(reconnectTimer)
    clearTimeout(reconnectFallbackTimer)
    reconnectTimer = null
    reconnectFallbackTimer = null
  }

  const stopStats = () => {
    clearInterval(statsTimer)
    statsTimer = null
  }

  const startSpeedTracking = () => {
    clearInterval(speedTimer)
    previousSpeedBytes = bytesTransferred.value
    previousSpeedTime = Date.now()

    speedTimer = setInterval(() => {
      const now = Date.now()
      const elapsedSeconds = Math.max(0.001, (now - previousSpeedTime) / 1000)
      const byteDifference = Math.max(0, bytesTransferred.value - previousSpeedBytes)

      speedBytesPerSecond.value = isTransferInProgress.value
        ? Math.round(byteDifference / elapsedSeconds)
        : 0
      previousSpeedBytes = bytesTransferred.value
      previousSpeedTime = now
    }, 1000)
  }

  const selectedCandidatePair = async () => {
    if (!peerConnection.value) {
      return null
    }

    const stats = await peerConnection.value.getStats()
    let pair = null

    for (const report of stats.values()) {
      if (report.type === 'transport' && report.selectedCandidatePairId) {
        pair = stats.get(report.selectedCandidatePairId) ?? pair
      }

      if (
        report.type === 'candidate-pair'
        && report.state === 'succeeded'
        && (report.nominated || report.selected)
      ) {
        pair = report
      }
    }

    if (!pair) {
      return null
    }

    return {
      local: stats.get(pair.localCandidateId),
      remote: stats.get(pair.remoteCandidateId),
    }
  }

  const refreshRouteType = async () => {
    try {
      const pair = await selectedCandidatePair()

      if (!pair?.local && !pair?.remote) {
        return
      }

      routeType.value = [pair.local?.candidateType, pair.remote?.candidateType].includes('relay')
        ? 'relayed'
        : 'direct'
    } catch {
      routeType.value = 'unknown'
    }
  }

  const startStats = () => {
    stopStats()
    refreshRouteType()
    statsTimer = setInterval(refreshRouteType, 2000)
  }

  const sendControlMessage = (message) => {
    if (dataChannel.value?.readyState !== 'open') {
      throw new Error('The peer data channel is not open.')
    }

    dataChannel.value.send(JSON.stringify(message))
  }

  const outgoingTransferMetadata = () => {
    if (!selectedFile.value || !outgoingMetadata.value) {
      return null
    }

    return {
      transferId: outgoingMetadata.value.transferId,
      fileName: sanitizedFileName(selectedFile.value.name),
      mimeType: selectedFile.value.type || 'application/octet-stream',
      size: selectedFile.value.size,
      chunkSize: transferSettings().chunkSize,
      lastModified: selectedFile.value.lastModified,
      fingerprint: outgoingMetadata.value.fingerprint,
      integrityAlgorithm: 'sha256-chain-v1',
    }
  }

  const sendMetadata = () => {
    const metadata = outgoingTransferMetadata()

    if (!metadata || dataChannel.value?.readyState !== 'open') {
      return
    }

    transferStatus.value = 'waiting-for-receiver'
    sendControlMessage({ type: 'metadata', metadata })
  }

  const waitForBackpressure = async (channel) => {
    const { maxBufferedAmount, bufferedAmountLowThreshold } = transferSettings()

    if (channel.bufferedAmount <= maxBufferedAmount) {
      return
    }

    channel.bufferedAmountLowThreshold = bufferedAmountLowThreshold

    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        cleanup()
        reject(new Error('The peer stopped accepting transfer data.'))
      }, 30_000)
      const handleLowBuffer = () => {
        cleanup()
        resolve()
      }
      const handleClose = () => {
        cleanup()
        reject(new Error('The data channel closed while waiting for backpressure.'))
      }
      const cleanup = () => {
        clearTimeout(timeout)
        channel.removeEventListener('bufferedamountlow', handleLowBuffer)
        channel.removeEventListener('close', handleClose)
      }

      channel.addEventListener('bufferedamountlow', handleLowBuffer, { once: true })
      channel.addEventListener('close', handleClose, { once: true })

      if (channel.bufferedAmount <= maxBufferedAmount) {
        handleLowBuffer()
      }
    })
  }

  const startSendingAt = async (requestedOffset) => {
    const file = selectedFile.value
    const metadata = outgoingTransferMetadata()
    const channel = dataChannel.value

    if (
      !file
      || !metadata
      || channel?.readyState !== 'open'
      || !validOffset(requestedOffset, file.size, metadata.chunkSize)
    ) {
      return
    }

    const generation = ++sendGeneration
    bytesTransferred.value = requestedOffset
    totalBytes.value = file.size
    transferStatus.value = requestedOffset > 0 ? 'resuming' : 'sending'
    integrityStatus.value = 'pending'

    try {
      if (senderHashedOffset !== requestedOffset) {
        transferStatus.value = requestedOffset > 0 ? 'verifying' : 'sending'
        senderIntegrityState = await integrityStateForFileOffset(
          file,
          requestedOffset,
          metadata.chunkSize
        )
        senderHashedOffset = requestedOffset
        transferStatus.value = requestedOffset > 0 ? 'resuming' : 'sending'
      }

      let offset = requestedOffset

      while (
        offset < file.size
        && generation === sendGeneration
        && channel.readyState === 'open'
      ) {
        await waitForBackpressure(channel)

        const nextOffset = Math.min(file.size, offset + metadata.chunkSize)
        const chunk = await file.slice(offset, nextOffset).arrayBuffer()
        const nextIntegrityState = await updateIntegrityChain(senderIntegrityState, chunk)

        if (generation !== sendGeneration || channel.readyState !== 'open') {
          return
        }

        channel.send(chunk)
        senderIntegrityState = nextIntegrityState
        senderHashedOffset = nextOffset
        offset = nextOffset
      }

      if (generation !== sendGeneration || channel.readyState !== 'open') {
        return
      }

      sendControlMessage({
        type: 'complete',
        transferId: metadata.transferId,
        integrity: senderIntegrityState,
        algorithm: metadata.integrityAlgorithm,
      })
      transferStatus.value = 'awaiting-verification'

      await saveRoomSession({
        roomId: transferStore.room.public_id,
        ...metadata,
        status: 'awaiting-verification',
      })
    } catch (error) {
      if (generation === sendGeneration) {
        setError(error)
      }
    }
  }

  const persistReceiverProgress = async (status = 'receiving') => {
    if (!incomingMetadata.value) {
      return
    }

    await saveTransferRecord({
      ...incomingMetadata.value,
      roomId: transferStore.room.public_id,
      receivedOffset: bytesTransferred.value,
      integrityState: receiverIntegrityState,
      storageKind: receiverStorage?.kind,
      status,
      updatedAt: new Date().toISOString(),
    })
  }

  const handleMetadata = async (rawMetadata) => {
    if (!isRecipient.value) {
      return
    }

    const metadata = normalizedMetadata(rawMetadata, transferSettings().maxFileSize)

    if (!metadata) {
      throw new Error('The sender supplied invalid file metadata.')
    }

    const existingRecord = await getTransferRecord(metadata.transferId)
    const canResume = metadataMatchesRecord(metadata, existingRecord)
      && existingRecord?.status !== 'integrity-failed'
    const requestedOffset = canResume
      ? Math.min(Number(existingRecord.receivedOffset ?? 0), metadata.size)
      : 0

    if (receiverStorage) {
      await receiverStorage.close()
    }

    receiverStorage = await openReceiverStorage(
      metadata,
      requestedOffset,
      canResume ? existingRecord?.storageKind : null
    )
    const confirmedOffset = receiverStorage.confirmedOffset

    incomingMetadata.value = metadata
    storageMode.value = receiverStorage.kind
    bytesTransferred.value = confirmedOffset
    totalBytes.value = metadata.size
    receiverIntegrityState = canResume
      && confirmedOffset === requestedOffset
      && /^[a-f0-9]{64}$/i.test(existingRecord.integrityState ?? '')
      ? existingRecord.integrityState
      : initialIntegrityState()
    downloadAvailable.value = false
    integrityStatus.value = 'pending'
    transferStatus.value = confirmedOffset > 0 ? 'resuming' : 'receiving'

    await persistReceiverProgress()
    await transferStore.sendSignal(SIGNAL_EVENT_TYPES.resumeRequest, {
      transfer_id: metadata.transferId,
      offset: confirmedOffset,
    })
  }

  const handleBinaryChunk = async (chunk) => {
    if (!isRecipient.value || !incomingMetadata.value || !receiverStorage) {
      throw new Error('File data arrived before valid transfer metadata.')
    }

    const bytes = chunk instanceof ArrayBuffer
      ? chunk
      : chunk instanceof Blob
        ? await chunk.arrayBuffer()
        : null

    if (!bytes) {
      throw new Error('The peer supplied an unsupported data channel message.')
    }

    const metadata = incomingMetadata.value
    const offset = bytesTransferred.value
    const nextOffset = offset + bytes.byteLength

    if (
      bytes.byteLength <= 0
      || bytes.byteLength > metadata.chunkSize
      || nextOffset > metadata.size
    ) {
      throw new Error('The peer supplied an invalid file chunk.')
    }

    const nextIntegrityState = await updateIntegrityChain(receiverIntegrityState, bytes)

    await receiverStorage.write(offset, bytes)

    receiverIntegrityState = nextIntegrityState
    bytesTransferred.value = nextOffset
    transferStatus.value = 'receiving'
    await persistReceiverProgress()
    sendControlMessage({
      type: 'ack',
      transferId: metadata.transferId,
      offset: nextOffset,
    })
  }

  const handleTransferComplete = async (message) => {
    if (
      !isRecipient.value
      || message?.transferId !== incomingMetadata.value?.transferId
      || !/^[a-f0-9]{64}$/i.test(message?.integrity ?? '')
    ) {
      return
    }

    transferStatus.value = 'verifying'

    if (
      bytesTransferred.value !== incomingMetadata.value.size
      || receiverIntegrityState !== message.integrity
    ) {
      integrityStatus.value = 'failed'
      transferStatus.value = 'failed'
      sendControlMessage({
        type: 'integrity-failed',
        transferId: incomingMetadata.value.transferId,
      })
      await persistReceiverProgress('integrity-failed')

      return
    }

    await receiverStorage.close()
    await persistReceiverProgress('completed')

    integrityStatus.value = 'verified'
    transferStatus.value = 'completed'
    downloadAvailable.value = true
    sendControlMessage({
      type: 'verified',
      transferId: incomingMetadata.value.transferId,
      integrity: receiverIntegrityState,
    })
  }

  const handleControlMessage = async (message) => {
    switch (message?.type) {
    case 'metadata':
      await handleMetadata(message.metadata)
      break
    case 'ack':
      if (
        isCreator.value
          && message.transferId === outgoingMetadata.value?.transferId
          && Number.isSafeInteger(message.offset)
      ) {
        bytesTransferred.value = Math.max(
          bytesTransferred.value,
          Math.min(message.offset, totalBytes.value)
        )
      }
      break
    case 'complete':
      await handleTransferComplete(message)
      break
    case 'verified':
      if (
        isCreator.value
          && message.transferId === outgoingMetadata.value?.transferId
          && message.integrity === senderIntegrityState
      ) {
        bytesTransferred.value = totalBytes.value
        integrityStatus.value = 'verified'
        transferStatus.value = 'completed'
        await deleteRoomSession(transferStore.room.public_id)
      }
      break
    case 'integrity-failed':
      if (isCreator.value && message.transferId === outgoingMetadata.value?.transferId) {
        integrityStatus.value = 'failed'
        transferStatus.value = 'failed'
        errorMessage.value = 'The receiver could not verify the completed file.'
      }
      break
    }
  }

  const handleDataChannelMessage = async (data) => {
    if (typeof data === 'string') {
      if (data.length > 16 * 1024) {
        throw new Error('The peer supplied an oversized control message.')
      }

      let message

      try {
        message = JSON.parse(data)
      } catch {
        throw new Error('The peer supplied an invalid control message.')
      }

      await handleControlMessage(message)
      return
    }

    await handleBinaryChunk(data)
  }

  const scheduleReconnect = () => {
    if (isCleaningUp || isReplacingConnection || transferStatus.value === 'completed') {
      return
    }

    clearTimeout(reconnectTimer)
    connectionState.value = 'reconnecting'

    if (isTransferInProgress.value) {
      transferStatus.value = 'reconnecting'
    }

    reconnectTimer = setTimeout(async () => {
      try {
        if (isCreator.value && peerConnection.value?.signalingState === 'stable') {
          await createOffer({ iceRestart: true, force: true })
        } else {
          await transferStore.sendSignal(SIGNAL_EVENT_TYPES.peerReady, outgoingMetadata.value ? {
            transfer_id: outgoingMetadata.value.transferId,
          } : {})
        }

        reconnectFallbackTimer = setTimeout(() => {
          if (
            !connectionReadyStates.has(peerConnection.value?.connectionState)
            || dataChannel.value?.readyState !== 'open'
          ) {
            recreatePeerConnection()
          }
        }, 8000)
      } catch {
        await recreatePeerConnection()
      }
    }, 1500)
  }

  const configureDataChannel = (channel) => {
    dataChannel.value = channel
    channel.binaryType = 'arraybuffer'
    channel.bufferedAmountLowThreshold = transferSettings().bufferedAmountLowThreshold

    channel.addEventListener('open', () => {
      clearConnectionTimers()
      connectionState.value = 'connected'
      startStats()

      if (isCreator.value && hasStartedTransfer.value) {
        sendMetadata()
      }
    })
    channel.addEventListener('close', scheduleReconnect)
    channel.addEventListener('error', () => scheduleReconnect())
    channel.addEventListener('message', (event) => {
      receiveQueue = receiveQueue
        .then(() => handleDataChannelMessage(event.data))
        .catch((error) => setError(error))
    })
  }

  const flushPendingIceCandidates = async () => {
    if (!peerConnection.value?.remoteDescription) {
      return
    }

    for (const candidate of pendingIceCandidates) {
      try {
        await peerConnection.value.addIceCandidate(candidate)
      } catch (error) {
        console.warn('Ignoring an ICE candidate that does not match the active negotiation.', error)
      }
    }

    pendingIceCandidates = []
  }

  const setupPeerConnection = () => {
    if (!window.RTCPeerConnection) {
      throw new Error('This browser does not support WebRTC peer connections.')
    }

    const connection = new RTCPeerConnection({
      iceServers: transferStore.room?.ice_servers ?? [],
    })

    peerConnection.value = connection
    pendingIceCandidates = []
    connectionState.value = 'waiting-for-peer'
    routeType.value = 'unknown'

    connection.addEventListener('icecandidate', (event) => {
      if (!event.candidate) {
        return
      }

      const candidate = event.candidate.toJSON()

      transferStore.sendSignal(SIGNAL_EVENT_TYPES.iceCandidate, {
        candidate: candidate.candidate ?? null,
        sdp_mid: candidate.sdpMid ?? null,
        sdp_m_line_index: candidate.sdpMLineIndex ?? null,
        username_fragment: candidate.usernameFragment ?? null,
      }).catch(() => scheduleReconnect())
    })

    connection.addEventListener('connectionstatechange', () => {
      if (connection.connectionState === 'connected') {
        connectionState.value = dataChannel.value?.readyState === 'open'
          ? 'connected'
          : 'connecting'
        clearConnectionTimers()
        startStats()
        return
      }

      connectionState.value = connection.connectionState

      if (['disconnected', 'failed'].includes(connection.connectionState)) {
        scheduleReconnect()
      }

      if (connection.connectionState === 'closed') {
        stopStats()
      }
    })

    connection.addEventListener('datachannel', (event) => {
      if (isRecipient.value) {
        configureDataChannel(event.channel)
      }
    })

    if (isCreator.value) {
      configureDataChannel(connection.createDataChannel('direct-secure-transfer', {
        ordered: true,
      }))
    }
  }

  async function createOffer(options = {}) {
    const force = options.force === true
    const offerOptions = options.iceRestart ? { iceRestart: true } : {}

    if (
      !isCreator.value
      || makingOffer
      || !peerConnection.value
      || peerConnection.value.signalingState !== 'stable'
      || (!force && connectionState.value === 'connected')
    ) {
      return
    }

    makingOffer = true
    connectionState.value = offerOptions.iceRestart ? 'reconnecting' : 'connecting'

    try {
      const offer = await peerConnection.value.createOffer(offerOptions)
      await peerConnection.value.setLocalDescription(offer)
      await transferStore.sendSignal(SIGNAL_EVENT_TYPES.offer, {
        type: peerConnection.value.localDescription.type,
        sdp: peerConnection.value.localDescription.sdp,
      })
    } finally {
      makingOffer = false
    }
  }

  const handleOffer = async (payload) => {
    if (isCreator.value || payload?.type !== 'offer' || typeof payload?.sdp !== 'string') {
      return
    }

    if (!peerConnection.value || peerConnection.value.signalingState === 'closed') {
      setupPeerConnection()
    }

    await peerConnection.value.setRemoteDescription(payload)
    await flushPendingIceCandidates()

    const answer = await peerConnection.value.createAnswer()
    await peerConnection.value.setLocalDescription(answer)
    await transferStore.sendSignal(SIGNAL_EVENT_TYPES.answer, {
      type: peerConnection.value.localDescription.type,
      sdp: peerConnection.value.localDescription.sdp,
    })

    if (dataChannel.value?.readyState === 'open') {
      connectionState.value = 'connected'
    }
  }

  const handleAnswer = async (payload) => {
    if (
      !isCreator.value
      || payload?.type !== 'answer'
      || typeof payload?.sdp !== 'string'
      || !peerConnection.value
    ) {
      return
    }

    await peerConnection.value.setRemoteDescription(payload)
    await flushPendingIceCandidates()

    if (dataChannel.value?.readyState === 'open') {
      connectionState.value = 'connected'
    }
  }

  const handleIceCandidate = async (payload) => {
    if (!peerConnection.value || typeof payload !== 'object') {
      return
    }

    const candidate = {
      candidate: payload.candidate ?? '',
      sdpMid: payload.sdp_mid ?? null,
      sdpMLineIndex: payload.sdp_m_line_index ?? null,
      usernameFragment: payload.username_fragment ?? null,
    }

    if (peerConnection.value.remoteDescription) {
      try {
        await peerConnection.value.addIceCandidate(candidate)
        return
      } catch {
        pendingIceCandidates.push(candidate)
        return
      }
    }

    pendingIceCandidates.push(candidate)
  }

  const handleSignal = async (event) => {
    try {
      switch (event?.type) {
      case SIGNAL_EVENT_TYPES.offer:
        await handleOffer(event.payload)
        break
      case SIGNAL_EVENT_TYPES.answer:
        await handleAnswer(event.payload)
        break
      case SIGNAL_EVENT_TYPES.iceCandidate:
        await handleIceCandidate(event.payload)
        break
      case SIGNAL_EVENT_TYPES.peerReady:
        await createOffer({ force: true })
        break
      case SIGNAL_EVENT_TYPES.resumeRequest:
        if (
          isCreator.value
            && event.payload?.transfer_id === outgoingMetadata.value?.transferId
        ) {
          await startSendingAt(Number(event.payload.offset))
        }
        break
      case SIGNAL_EVENT_TYPES.transferCancelled:
        if (
          !event.payload?.transfer_id
            || [
              outgoingMetadata.value?.transferId,
              incomingMetadata.value?.transferId,
            ].includes(event.payload.transfer_id)
        ) {
          sendGeneration += 1
          transferStatus.value = 'cancelled'
        }
        break
      }
    } catch (error) {
      setError(error, 'WebRTC negotiation failed.')
    }
  }

  const closePeerConnection = () => {
    stopStats()

    if (dataChannel.value && dataChannel.value.readyState !== 'closed') {
      dataChannel.value.close()
    }

    if (peerConnection.value && peerConnection.value.connectionState !== 'closed') {
      peerConnection.value.close()
    }

    dataChannel.value = null
    peerConnection.value = null
  }

  async function recreatePeerConnection() {
    if (isCleaningUp || !isParticipant.value) {
      return
    }

    isReplacingConnection = true
    clearConnectionTimers()
    closePeerConnection()

    try {
      setupPeerConnection()
      connectionState.value = 'reconnecting'

      if (isCreator.value) {
        await createOffer()
      } else {
        await transferStore.sendSignal(SIGNAL_EVENT_TYPES.peerReady, incomingMetadata.value ? {
          transfer_id: incomingMetadata.value.transferId,
        } : {})
      }
    } catch (error) {
      setError(error, 'Unable to recreate the peer connection.')
    } finally {
      isReplacingConnection = false
    }
  }

  const handlePresence = (users) => {
    if (users.length >= 2) {
      connectionState.value = 'connecting'

      if (isCreator.value) {
        createOffer().catch((error) => setError(error, 'WebRTC negotiation failed.'))
      }

      return
    }

    if (!isTransferInProgress.value) {
      connectionState.value = 'waiting-for-peer'
    }
  }

  const activateRoom = () => {
    if (!isParticipant.value) {
      return
    }

    setupPeerConnection()
    startSpeedTracking()
    transferStore.subscribeToRoom({
      transferId: () => outgoingMetadata.value?.transferId ?? incomingMetadata.value?.transferId,
      presenceChanged: handlePresence,
      peerAvailable: () => {
        if (isCreator.value) {
          createOffer().catch((error) => setError(error, 'WebRTC negotiation failed.'))
        }
      },
      peerJoined: () => {
        connectionState.value = 'connecting'
      },
      peerLeft: () => {
        connectionState.value = 'waiting-for-peer'

        if (isTransferInProgress.value) {
          transferStatus.value = 'reconnecting'
        }
      },
      signalReceived: handleSignal,
      signalFailed: (error) => setError(error, 'Unable to signal the peer.'),
    })
  }

  const resetTransferState = () => {
    sendGeneration += 1
    selectedFile.value = null
    outgoingMetadata.value = null
    incomingMetadata.value = null
    transferStatus.value = 'idle'
    bytesTransferred.value = 0
    totalBytes.value = 0
    speedBytesPerSecond.value = 0
    integrityStatus.value = 'pending'
    downloadAvailable.value = false
    storageMode.value = null
    hasStartedTransfer.value = false
    selectedFileCanResume.value = false
    receiverIntegrityState = initialIntegrityState()
    senderIntegrityState = initialIntegrityState()
    senderHashedOffset = 0
    receiveQueue = Promise.resolve()
  }

  const releaseCurrentRoom = async (resetTransfer = true) => {
    isCleaningUp = true
    clearConnectionTimers()
    clearInterval(speedTimer)
    speedTimer = null
    transferStore.unsubscribeFromRoom()
    closePeerConnection()

    if (receiverStorage) {
      try {
        await receiverStorage.close()
      } catch (error) {
        console.warn('Unable to close partial transfer storage cleanly.', error)
      }
      receiverStorage = null
    }

    if (resetTransfer) {
      resetTransferState()
    }

    isCleaningUp = false
  }

  const initializeRoom = async (publicId) => {
    await releaseCurrentRoom()
    transferStore.resetRoom()
    errorMessage.value = ''
    connectionState.value = 'loading'

    try {
      const room = await transferStore.fetchRoom(publicId)

      if (['creator', 'recipient'].includes(room.role)) {
        activateRoom()
      } else {
        connectionState.value = 'waiting-for-acceptance'
      }

      return room
    } catch (error) {
      connectionState.value = 'failed'
      setError(error, 'Unable to open this transfer room.')
      throw error
    }
  }

  const createRoom = async () => {
    errorMessage.value = ''

    try {
      return await transferStore.createRoom()
    } catch (error) {
      setError(error, 'Unable to create a transfer room.')
      throw error
    }
  }

  const acceptRoom = async () => {
    errorMessage.value = ''

    try {
      await transferStore.joinRoom(transferStore.room.public_id)
      activateRoom()
    } catch (error) {
      setError(error, 'Unable to join this transfer room.')
      throw error
    }
  }

  const selectFile = async (file) => {
    errorMessage.value = ''
    transferStatus.value = 'preparing'

    if (!file) {
      resetTransferState()
      return
    }

    const { maxFileSize, chunkSize } = transferSettings()

    if (file.size > maxFileSize) {
      transferStatus.value = 'idle'
      throw new Error(`The selected file exceeds the ${maxFileSize} byte transfer limit.`)
    }

    try {
      const fingerprint = await fingerprintFile(file)
      const roomSession = await getRoomSession(transferStore.room.public_id)
      const canResume = roomSession
        && roomSession.size === file.size
        && roomSession.lastModified === file.lastModified
        && roomSession.fingerprint === fingerprint
      const transferId = canResume ? roomSession.transferId : crypto.randomUUID()
      const metadata = {
        transferId,
        fileName: sanitizedFileName(file.name),
        mimeType: file.type || 'application/octet-stream',
        size: file.size,
        chunkSize,
        lastModified: file.lastModified,
        fingerprint,
        integrityAlgorithm: 'sha256-chain-v1',
      }

      selectedFile.value = file
      outgoingMetadata.value = metadata
      selectedFileCanResume.value = Boolean(canResume)
      totalBytes.value = file.size
      bytesTransferred.value = 0
      senderIntegrityState = initialIntegrityState()
      senderHashedOffset = 0
      transferStatus.value = 'ready'
      integrityStatus.value = 'pending'

      await saveRoomSession({
        roomId: transferStore.room.public_id,
        ...metadata,
        status: canResume ? roomSession.status : 'ready',
      })
    } catch (error) {
      selectedFile.value = null
      outgoingMetadata.value = null
      transferStatus.value = 'failed'
      setError(error, 'Unable to prepare the selected file.')
      throw error
    }
  }

  const startTransfer = async () => {
    if (!selectedFile.value || !outgoingMetadata.value) {
      throw new Error('Select a file before starting the transfer.')
    }

    if (dataChannel.value?.readyState !== 'open') {
      throw new Error('Wait for the peer connection before starting the transfer.')
    }

    hasStartedTransfer.value = true
    await saveRoomSession({
      roomId: transferStore.room.public_id,
      ...outgoingMetadata.value,
      status: 'sending',
    })
    sendMetadata()
  }

  const cancelTransfer = async () => {
    sendGeneration += 1
    hasStartedTransfer.value = false
    transferStatus.value = 'cancelled'
    speedBytesPerSecond.value = 0

    if (isParticipant.value) {
      try {
        await transferStore.sendSignal(SIGNAL_EVENT_TYPES.transferCancelled, {
          transfer_id: outgoingMetadata.value?.transferId ?? incomingMetadata.value?.transferId,
          reason: 'Cancelled by the peer.',
        })
      } catch (error) {
        errorMessage.value = errorMessageFrom(error, 'The local transfer was cancelled.')
      }
    }
  }

  const saveReceivedFile = async () => {
    if (!downloadAvailable.value || !receiverStorage || !incomingMetadata.value) {
      return
    }

    await receiverStorage.save(
      sanitizedFileName(incomingMetadata.value.fileName),
      incomingMetadata.value.mimeType
    )
  }

  onUnmounted(() => {
    releaseCurrentRoom()
    transferStore.resetRoom()
  })

  return {
    transferStore,
    selectedFile,
    outgoingMetadata,
    incomingMetadata,
    connectionState,
    transferStatus,
    routeType,
    bytesTransferred,
    totalBytes,
    speedBytesPerSecond,
    progress,
    integrityStatus,
    errorMessage,
    downloadAvailable,
    storageMode,
    selectedFileCanResume,
    isCreator,
    isRecipient,
    isParticipant,
    isConnected,
    isTransferInProgress,
    initializeRoom,
    createRoom,
    acceptRoom,
    selectFile,
    startTransfer,
    cancelTransfer,
    saveReceivedFile,
    releaseCurrentRoom,
  }
}
